import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';
import type { createNewsModule } from 'news';

import type { createLegacyCommunicationsModule } from './communications';
import type { createConnectionsModule } from './connections';
import type { createContactsModule } from './contacts';
import type { createDocumentsModule } from './documents';
import type { createHistoryLogModule } from './historyLog';
import type { createNabanModule } from './naban';
import type { createNotaryModule } from './notary';
import type { createNotificationsModule } from './notifications';
import type { createPostalCodesModule } from './postalCodes';
import type { createPublicModule } from './public';
import type { createSharedModule } from './shared';
import type { createStudyModule } from './study';
import type { createTermsAndConditionsModule } from './termsAndConditions';
import type { createUserModule } from './user';
import type { createUserMessagesModule } from './userMessages';
import type { createVaultModule } from './vault';

interface BaseModules {
  contacts: ReturnType<typeof createContactsModule>;
  communications: ReturnType<typeof createLegacyCommunicationsModule>;
  documents: ReturnType<typeof createDocumentsModule>;
  public: ReturnType<typeof createPublicModule>;
  notary: ReturnType<typeof createNotaryModule>;
  user: ReturnType<typeof createUserModule>;
  historyLog: ReturnType<typeof createHistoryLogModule>;
  postalCodes: ReturnType<typeof createPostalCodesModule>;
  vault: ReturnType<typeof createVaultModule>;
  notifications: ReturnType<typeof createNotificationsModule>;
  shared: ReturnType<typeof createSharedModule>;
  study: ReturnType<typeof createStudyModule>;
}

export type NotaryModules = BaseModules;

export type CitizenModules = BaseModules & {
  connections: ReturnType<typeof createConnectionsModule>;
  naban: ReturnType<typeof createNabanModule>;
  termsAndConditions: ReturnType<typeof createTermsAndConditionsModule>;
  news: ReturnType<typeof createNewsModule>;
  userMessages: ReturnType<typeof createUserMessagesModule>;
};

type Modules = CitizenModules | NotaryModules;

const ModulesContext = createContext<Modules | null>(null);

export const useModules = () => {
  const context = useContext(ModulesContext);
  if (!context) {
    throw new Error('Did you forget to wrap with ModulesProvider?');
  }
  return context;
};

export const ModulesProvider = ({
  children,
  modules,
}: {
  children: ReactNode;
  modules: Modules;
}) => {
  return <ModulesContext.Provider value={modules}>{children}</ModulesContext.Provider>;
};
